import { SortOrdersType } from "@/types/sort.type";
import { PaymentHistory } from "@/types/payment-history.type";
import { createContext, ReactNode, useState, useContext, useEffect, useMemo } from "react";
import { SortOrder } from "@/types/gql/graphql";
import { initPagination } from "@/modules/finance/helper/constants/init-pagination.const";
import { useSetQueryParams } from "@/hooks/use-set-query-params";
import { getQueryParams } from "@/helper/utils";
import { PaginationParams } from "@/types";
import { CityContext } from "@/contexts/city-context-provider";

export type FinanceFilter = SortOrdersType<PaymentHistory> & {
  setRange: (newRange: { startDate: Date; endDate: Date }) => void;
  setCurrentPage: (newPage: number) => void;
  pageIndex: number;
  pageSize: number;
  startDate: Date;
  endDate: Date;
  setSortOrder: (sort: SortOrdersType<PaymentHistory>) => void;
};

type FinanceFilterUrlParams = {
  startDate: string | undefined;
  endDate: string | undefined;
  pageIndex: string | undefined;
  sortField: string | undefined;
  sortOrder: string | undefined;
};

export const FinanceFilterContext = createContext<FinanceFilter | null>(null);

export const FinanceFilterProvider = ({ children }: { children: ReactNode }) => {
  const setQueryParams = useSetQueryParams();
  const {
    startDate: initialStartDate,
    endDate: initialEndDate,
    sortField: initialSortField,
    sortOrder: initialSortOrder,
    pageIndex: initialPageIndex,
  } = getQueryParams<FinanceFilterUrlParams>();

  const { currentCity } = useContext(CityContext);

  // Ensure the selected city is preserved in the URL and stays in sync.
  // The city may already be selected in state, but missing from query params.
  // We only write cityID once a valid currentCity.id is available.
  useEffect(() => {
    if (!currentCity?.id) {
      return;
    }

    setQueryParams({ cityID: currentCity.id }, { merge: true });
  }, [setQueryParams, currentCity?.id]);

  const now = new Date();

  const defaultFinanceDateRange = {
    startDate: initialStartDate
      ? new Date(Number(initialStartDate))
      : new Date(now.getFullYear(), now.getMonth(), 1, 0, 0),
    endDate: initialEndDate
      ? new Date(Number(initialEndDate))
      : new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59),
  };

  const defaultSortDetails: SortOrdersType<PaymentHistory> = {
    sortOrder: initialSortOrder ? (initialSortOrder as unknown as SortOrder) : SortOrder.Desc,
    sortField: (initialSortField as unknown as keyof PaymentHistory) || "start_date",
  };

  const defaultPageDetails = {
    pageSize: initPagination.pageSize,
    pageIndex: initialPageIndex ? Number(initialPageIndex) : initPagination.pageIndex,
  };

  const [range, setRange] = useState(defaultFinanceDateRange);
  const [currentPage, setCurrentPage] = useState<PaginationParams>(defaultPageDetails);
  const [sortOrders, setSortOrder] = useState<SortOrdersType<PaymentHistory>>(defaultSortDetails);

  const value: FinanceFilter = useMemo(() => {
    return {
      startDate: range.startDate,
      endDate: range.endDate,
      setRange: (newRange) => {
        setQueryParams(
          {
            startDate: String(newRange.startDate.getTime()),
            endDate: String(newRange.endDate.getTime()),
          },
          { merge: true },
        );
        setRange(newRange);
      },
      setSortOrder: (data) => {
        setQueryParams({ ...data }, { merge: true });
        setSortOrder(data);
      },
      ...currentPage,
      ...sortOrders,
      setCurrentPage: (pageIndex) => {
        setQueryParams({ pageIndex: String(pageIndex) }, { merge: true });
        setCurrentPage((prev) => ({ ...prev, pageIndex }));
      },
    };
  }, [range, setQueryParams, sortOrders, currentPage]);

  return <FinanceFilterContext.Provider value={value}>{children}</FinanceFilterContext.Provider>;
};
